/**
 * Insights Tab Component
 * Traffic light system consolidating urgent actions, performance alerts, and opportunities
 * into a single tab with red/yellow/green grouping.
 */

import React from 'react';
import {
  AssetAllocation,
  RiskMetrics,
  StressTests,
  PerformanceMetrics,
} from '../../services/api/portfolioApiService';
import { useCanonicalDrift } from '../../hooks/useCanonicalDrift';

interface PortfolioInsightsTabProps {
  allocation?: AssetAllocation | null;
  risk?: RiskMetrics | null;
  stressTests?: StressTests | null;
  performance?: PerformanceMetrics | null;
  cash?: number;
  portfolioValue?: number;
  /**
   * TASK-723 (review 2026-07-29): required to reach the canonical drift decision. Without it
   * drift is reported as unassessed rather than assumed clean — the tab never claims a
   * verdict it was not given.
   */
  clientId?: string | null;
  onAction: (action: string, data: any) => void;
  loading?: boolean;
}

interface InsightItem {
  text: string;
  buttonText: string;
  buttonAction: () => void;
}

type TrafficLevel = 'red' | 'yellow' | 'green';

interface TrafficSection {
  level: TrafficLevel;
  label: string;
  items: InsightItem[];
  borderColor: string;
  dotColor: string;
  headerColor: string;
  bgColor: string;
}

const PortfolioInsightsTab: React.FC<PortfolioInsightsTabProps> = ({
  allocation,
  risk,
  stressTests: _stressTests,
  performance,
  cash,
  portfolioValue,
  clientId,
  onAction,
  loading,
}) => {
  // TASK-723 (review 2026-07-29): this tab read `allocation.rebalance_needed`, which the
  // canonical-drift work retired — it is now absent on the happy path and `null` when the
  // risk profile is incomplete, so the drift insight silently stopped appearing and the tab
  // showed "All clear" for a client the Radar was raising `portfolio_drift_breach` for.
  const { needsRebalancing, state: driftState, note: driftNote, breachedSlots } =
    useCanonicalDrift(clientId);

  const getRedItems = (): InsightItem[] => {
    const items: InsightItem[] = [];

    if (risk && risk.concentration_risk > 15) {
      items.push({
        text: `Concentration risk at ${risk.concentration_risk.toFixed(1)}%`,
        buttonText: 'Resolve',
        buttonAction: () => onAction('rebalance', { type: 'concentration' }),
      });
    }

    if (cash && portfolioValue) {
      const cashPercentage = (cash / portfolioValue) * 100;
      if (cashPercentage < 2) {
        items.push({
          text: `Low liquidity: ${cashPercentage.toFixed(1)}%`,
          buttonText: 'Increase Cash',
          buttonAction: () => onAction('increase-cash', { current: cashPercentage }),
        });
      }
    }

    if (needsRebalancing === true) {
      items.push({
        text:
          breachedSlots.length > 0
            ? `Portfolio rebalancing required — ${breachedSlots.length} allocation ` +
              `${breachedSlots.length === 1 ? 'slot is' : 'slots are'} outside tolerance`
            : 'Portfolio rebalancing required',
        buttonText: 'Create Task',
        buttonAction: () => onAction('rebalance', { allocation }),
      });
    }

    return items;
  };

  const getYellowItems = (): InsightItem[] => {
    const items: InsightItem[] = [];

    // An unassessable allocation is a WARNING, not silence. Left out, the tab falls through
    // to its "All clear" banner and reports the absence of an analysis as its result.
    if (driftState !== 'loading' && needsRebalancing === null) {
      items.push({
        text:
          driftState === 'failed'
            ? `Allocation drift could not be assessed${driftNote ? ` — ${driftNote}` : ''}`
            : `Allocation drift cannot be assessed${driftNote ? ` — ${driftNote}` : ''}`,
        buttonText: 'Review Profile',
        buttonAction: () => onAction('review', { metric: 'drift' }),
      });
    }

    if (performance) {
      if (performance.total_return != null && performance.total_return < 0) {
        items.push({
          text: `Portfolio down ${Math.abs(performance.total_return).toFixed(1)}%`,
          buttonText: 'Run Analysis',
          buttonAction: () => onAction('analyze', { type: 'performance' }),
        });
      }

      if (performance.sharpe_ratio != null && performance.sharpe_ratio < 0.5) {
        items.push({
          text: `Low Sharpe ratio: ${performance.sharpe_ratio.toFixed(2)}`,
          buttonText: 'Analyze',
          buttonAction: () => onAction('review', { metric: 'sharpe' }),
        });
      }

      if (performance.volatility != null && performance.volatility > 20) {
        items.push({
          text: `High volatility: ${performance.volatility.toFixed(1)}%`,
          buttonText: 'Stabilize',
          buttonAction: () => onAction('stabilize', { volatility: performance.volatility }),
        });
      }
    }

    return items;
  };

  const getGreenItems = (): InsightItem[] => {
    const items: InsightItem[] = [];

    if (performance && performance.total_return != null && performance.total_return < -5) {
      items.push({
        text: 'Tax loss harvesting available',
        buttonText: 'Run Scenario',
        buttonAction: () => onAction('tax-loss-harvesting', {}),
      });
    }

    if (needsRebalancing === true) {
      items.push({
        text: 'Rebalancing opportunity detected',
        buttonText: 'Run Scenario',
        buttonAction: () => onAction('strategic-rebalance', { allocation }),
      });
    }

    return items;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i}>
              <div className="h-5 bg-gray-200 rounded w-32 mb-3" />
              <div className="space-y-2">
                <div className="h-10 bg-gray-100 rounded" />
                <div className="h-10 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const sections: TrafficSection[] = [
    {
      level: 'red',
      label: 'Critical',
      items: getRedItems(),
      borderColor: 'border-red-500',
      dotColor: 'bg-red-500',
      headerColor: 'text-red-700',
      bgColor: 'bg-red-50',
    },
    {
      level: 'yellow',
      label: 'Warnings',
      items: getYellowItems(),
      borderColor: 'border-yellow-500',
      dotColor: 'bg-yellow-500',
      headerColor: 'text-yellow-700',
      bgColor: 'bg-yellow-50',
    },
    {
      level: 'green',
      label: 'Opportunities',
      items: getGreenItems(),
      borderColor: 'border-green-500',
      dotColor: 'bg-green-500',
      headerColor: 'text-green-700',
      bgColor: 'bg-green-50',
    },
  ];

  const hasRedOrYellow = sections[0].items.length > 0 || sections[1].items.length > 0;
  const visibleSections = sections.filter((s) => s.items.length > 0);

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      {/*
        The green banner is a VERDICT, so it may only render once every input has answered.
        While drift is still loading `needsRebalancing` is null and no warning has been pushed
        yet — without this guard the tab flashed "All clear" over an analysis that had not
        finished. The copy also states the scope it actually covers rather than implying the
        whole book of checks (TASK-722/725).
      */}
      {!hasRedOrYellow && driftState === 'loading' && (
        <div className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <span className="inline-block w-3 h-3 rounded-full bg-gray-400 animate-pulse" />
          <span className="text-sm font-medium text-gray-700">
            Checking allocation drift…
          </span>
        </div>
      )}
      {!hasRedOrYellow && driftState !== 'loading' && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
          <span className="text-sm font-medium text-green-800">
            All clear — no critical issues or warnings in the allocation, risk, liquidity and
            performance checks.
          </span>
        </div>
      )}

      {visibleSections.map((section) => (
        <div
          key={section.level}
          className={`border-l-4 ${section.borderColor} ${section.bgColor} rounded-r-lg`}
        >
          <div className="px-5 py-3">
            <h3 className={`text-sm font-semibold uppercase tracking-wide ${section.headerColor}`}>
              {section.label}
            </h3>
          </div>
          <div className="px-5 pb-4 space-y-1">
            {section.items.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 py-2.5 border-t border-white/60 first:border-t-0"
              >
                <span className={`inline-block w-2 h-2 rounded-full ${section.dotColor} shrink-0`} />
                <span className="flex-1 text-sm text-gray-700">{item.text}</span>
                <button
                  onClick={() => item.buttonAction()}
                  className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded bg-[#002B45] text-white hover:bg-[#003d63] transition-colors"
                >
                  {item.buttonText}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PortfolioInsightsTab;
